import { useCallback, useRef } from "react";
import { RunBackplanningDto, WorkflowResultDtoWorkflowNameEnum } from "../api-client";
import { useApi } from "../api/ApiProvider";
import useStore from "../helpers/useStore";
import { MutationDraftContext } from "../stores/MutationDraft/mutationDraft.provider";
import { AgentChatContext } from "../stores/AgentChat/agentChat.provider";
import { useLocationId } from "../stores/User/User.hooks";
import { useWorkflow } from "./useWorkflow";

interface UseBackPlanningAgentOptions {
  onCompleted?: () => void;
}

/**
 * Hook that drives the Backplanning Agent workflow.
 *
 * Clears previous state, fires `scheduleBackplanningWorkflow`, subscribes
 * to progress/result events, and updates draft + chat stores.
 */
export const useBackPlanningAgent = (options: UseBackPlanningAgentOptions = {}) => {
  const onCompletedRef = useRef(options.onCompleted);
  onCompletedRef.current = options.onCompleted;
  const { agentTools } = useApi();
  const locationId = useLocationId();

  const mutationStoreRegistry = useStore(MutationDraftContext);
  const chatStoreRegistry = useStore(AgentChatContext);

  const draftStore = mutationStoreRegistry.getDefaultStore();
  const chatStore = chatStoreRegistry.getDefaultStore();

  const {
    workflowLoading,
    displayProgress,
    subscribeToWorkflow,
    reset: resetWorkflow,
  } = useWorkflow({
    workflowName: WorkflowResultDtoWorkflowNameEnum.BackplanningWorkflow,

    onResult: () => {
      draftStore.agent.setCompleted({} as any);
      chatStore.removeLoading();
      chatStore.add({
        role: "assistant",
        content: "Backplanning abgeschlossen.",
        timestamp: new Date(),
      });
      onCompletedRef.current?.();
    },

    onProgress: (progressDto) => {
      draftStore.agent.setProgress(progressDto.percent ?? 0);
    },

    onError: (errorMessage) => {
      draftStore.agent.setError(errorMessage);
      chatStore.removeLoading();
      chatStore.add({
        role: "system",
        content: `Fehler: ${errorMessage}`,
        timestamp: new Date(),
      });
    },

    showErrorNotifications: false,
  });

  const startBackPlanning = useCallback(
    async (options: { endDate?: string; selectedRxId?: string; changedCaIds?: string[] } = {}) => {
      if (!locationId) return;

      const dto: RunBackplanningDto = {
        locationId,
        ...(options.endDate && { endDate: options.endDate }),
        ...(options.selectedRxId && { selectedRxId: options.selectedRxId }),
        ...(options.changedCaIds && { changedCaIds: options.changedCaIds }),
      };

      // Clear previous state for a fresh start
      draftStore.clearAll();
      chatStore.clear();
      resetWorkflow();

      draftStore.agent.setRunning("backplanning");

      chatStore.add({
        role: "assistant",
        content: "Backplanning-Agent gestartet — Dauertermine werden verarbeitet…",
        timestamp: new Date(),
        isLoading: true,
      });

      try {
        console.log("Starting backplanning workflow with DTO:", dto);

        const job = await agentTools.scheduleBackplanningWorkflow(dto);
        if (job) {
          draftStore.agent.setWorkflow(job);
          subscribeToWorkflow(job);
        } else {
          draftStore.agent.setError("Workflow konnte nicht gestartet werden.");
          chatStore.removeLoading();
          chatStore.add({
            role: "system",
            content: "Fehler: Workflow konnte nicht gestartet werden.",
            timestamp: new Date(),
          });
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || "Unbekannter Fehler";
        draftStore.agent.setError(msg);
        chatStore.removeLoading();
        chatStore.add({
          role: "system",
          content: `Fehler: ${msg}`,
          timestamp: new Date(),
        });
      }
    },
    [locationId, agentTools, draftStore, chatStore, subscribeToWorkflow, resetWorkflow],
  );

  return { startBackPlanning, workflowLoading, displayProgress };
};
